import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { TipoPergola } from '../config/types';

/** Z de la cara frontal del muro de la casa (las pérgolas adosadas se pegan aquí). */
export const WALL_Z = -2.2;
const BODY_W = 12;
const BODY_H = 3.7;
const BODY_D = 7.5;

/**
 * Desplazamiento en Z del conjunto pérgola+cotas:
 * - adosada: el fondo (z=-L/2 local) se pega al muro de la casa.
 * - independiente: se separa hacia el jardín (delante de la casa).
 */
export function stageOffsetZ(tipo: TipoPergola, lengthMm: number): number {
  const L = lengthMm / 1000;
  return tipo === 'adosada' ? WALL_Z + L / 2 : 1.6;
}

/** Fondo de cielo (degradado). `night=true` para cielo nocturno. */
export function buildSkyBackground(night = false): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = 2;
  c.height = 256;
  const ctx = c.getContext('2d')!;
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  if (night) {
    g.addColorStop(0, '#0a0e18');
    g.addColorStop(0.6, '#141c2b');
    g.addColorStop(1, '#1f2a3c');
  } else {
    g.addColorStop(0, '#c6d4e3');
    g.addColorStop(0.55, '#e6ecf2');
    g.addColorStop(1, '#f7f9fb');
  }
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 2, 256);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Terreno amplio en tono neutro con un desnivel suave (ondulación) lejos de la
 * pérgola; permanece plano en un radio central para que la pérgola asiente bien.
 */
export function createTerrain(): THREE.Mesh {
  const size = 160;
  const seg = 80;
  const geo = new THREE.PlaneGeometry(size, size, seg, seg);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    pos.setZ(i, terrainDisplacement(pos.getX(i), pos.getY(i)));
  }
  geo.computeVertexNormals();

  const mat = new THREE.MeshStandardMaterial({ color: 0xf6f5f3, roughness: 0.98, metalness: 0 });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = -0.012;
  mesh.receiveShadow = true;
  mesh.name = 'terrain';
  return mesh;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** Desplazamiento del terreno en coords locales del plano (plano en el centro). */
function terrainDisplacement(lx: number, ly: number): number {
  const d = Math.hypot(lx, ly);
  const falloff = smoothstep(10, 40, d); // 0 cerca del centro, 1 lejos (desnivel gradual)
  const h =
    Math.sin(lx * 0.12) * Math.cos(ly * 0.1) * 0.28 +
    Math.sin(lx * 0.045 + 1.3) * Math.cos(ly * 0.06 - 0.7) * 0.6;
  return h * falloff;
}

/** Altura del terreno en coordenadas de mundo (X, Z). */
export function terrainHeightAt(x: number, z: number): number {
  return terrainDisplacement(x, -z); // el plano rota -90° en X: localY = -worldZ
}

/** Conjunto del entorno: terreno + casa + árboles (todo menos la pérgola). */
export function buildWorld(includeTrees = true): THREE.Group {
  const group = new THREE.Group();
  group.name = 'world';
  group.add(createTerrain());
  group.add(buildHouse());
  if (includeTrees) group.add(buildTrees());
  return group;
}

// ---------------- casa ----------------
function buildHouse(): THREE.Group {
  const house = new THREE.Group();
  house.name = 'house';

  const wallMat = new THREE.MeshStandardMaterial({ color: 0xeae9e5, roughness: 0.92, metalness: 0 });
  const roofMat = new THREE.MeshStandardMaterial({ color: 0xd6d8db, roughness: 0.85, metalness: 0 });
  const trimMat = new THREE.MeshStandardMaterial({ color: 0xcfd0cd, roughness: 0.8 });
  const plinthMat = new THREE.MeshStandardMaterial({ color: 0x9b9a96, roughness: 0.95 });
  const glassMat = new THREE.MeshStandardMaterial({
    color: 0xaebfce,
    roughness: 0.12,
    metalness: 0.15,
    transparent: true,
    opacity: 0.5,
  });
  const frameMat = new THREE.MeshStandardMaterial({ color: 0xbdc1c6, roughness: 0.7, metalness: 0.2 });

  const bodyCenterZ = WALL_Z - BODY_D / 2;
  const frontZ = WALL_Z + 0.04;

  // Plinto (base).
  const plinth = new THREE.Mesh(new THREE.BoxGeometry(BODY_W + 0.18, 0.4, BODY_D + 0.18), plinthMat);
  plinth.position.set(0, 0.2, bodyCenterZ);
  plinth.receiveShadow = true;
  house.add(plinth);

  // Cuerpo.
  const body = new THREE.Mesh(new THREE.BoxGeometry(BODY_W, BODY_H, BODY_D), wallMat);
  body.position.set(0, BODY_H / 2 + 0.1, bodyCenterZ);
  body.castShadow = true;
  body.receiveShadow = true;
  house.add(body);

  // Fascia (banda bajo el alero).
  const fascia = new THREE.Mesh(new THREE.BoxGeometry(BODY_W + 0.1, 0.18, BODY_D + 0.1), trimMat);
  fascia.position.set(0, BODY_H + 0.02, bodyCenterZ);
  house.add(fascia);

  // Tejado a dos aguas (prisma triangular) con alero.
  const eave = 0.45;
  const roofH = 1.9;
  const hw = BODY_W / 2 + eave;
  const shape = new THREE.Shape();
  shape.moveTo(-hw, 0);
  shape.lineTo(hw, 0);
  shape.lineTo(0, roofH);
  shape.closePath();
  const depth = BODY_D + eave * 2;
  const roofGeo = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
  roofGeo.translate(0, 0, -depth / 2);
  const roof = new THREE.Mesh(roofGeo, roofMat);
  roof.position.set(0, BODY_H + 0.1, bodyCenterZ);
  roof.castShadow = true;
  house.add(roof);

  // Chimenea.
  const chimney = new THREE.Mesh(new THREE.BoxGeometry(0.55, 1.5, 0.55), roofMat);
  chimney.position.set(3.4, BODY_H + 1.0, bodyCenterZ - 0.8);
  chimney.castShadow = true;
  house.add(chimney);

  const f = 0.07;
  const addBar = (w: number, h: number, x: number, y: number) => {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.1), frameMat);
    bar.position.set(x, y, frontZ + 0.02);
    house.add(bar);
  };

  // Gran cristalera DESPLAZADA a la derecha: deja LIMPIA la pared central (donde
  // apoya la pérgola adosada) y no la toca.
  const gW = 2.8;
  const gH = 2.9;
  const gx = 4.4;
  const gy = gH / 2 + 0.32;
  const glass = new THREE.Mesh(new THREE.BoxGeometry(gW, gH, 0.04), glassMat);
  glass.position.set(gx, gy, frontZ);
  house.add(glass);
  addBar(gW, f, gx, gy + gH / 2);
  addBar(gW, f, gx, gy - gH / 2);
  addBar(f, gH, gx - gW / 2, gy);
  addBar(f, gH, gx + gW / 2, gy);
  addBar(f, gH, gx, gy); // montante central (dos hojas)
  addBar(gW, f, gx, gy); // travesaño

  // Ventana izquierda (elaboración) con cruceta.
  const wW = 1.7;
  const wH = 1.3;
  const wx = -4.0;
  const wy = 1.7;
  const win = new THREE.Mesh(new THREE.BoxGeometry(wW, wH, 0.04), glassMat);
  win.position.set(wx, wy, frontZ);
  house.add(win);
  addBar(wW, f, wx, wy + wH / 2);
  addBar(wW, f, wx, wy - wH / 2);
  addBar(f, wH, wx - wW / 2, wy);
  addBar(f, wH, wx + wW / 2, wy);
  addBar(wW, f, wx, wy);
  addBar(f, wH, wx, wy);

  // Ventana alta pequeña (junto a la cristalera).
  const w2 = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.8, 0.04), glassMat);
  w2.position.set(1.6, 2.7, frontZ);
  house.add(w2);

  return house;
}

// ---------------- árboles (procedurales con ramas + copa) ----------------
function buildTrees(): THREE.Group {
  const trees = new THREE.Group();
  trees.name = 'trees';
  // Lejos de la cámara (≈7, 9) y sin tapar la vista frontal a la pérgola.
  const positions: Array<[number, number, number, number]> = [
    [-9, 1, 1.15, 11],
    [-6.5, -5, 0.95, 27],
    [-11, 5, 0.85, 53],
    [-6, -10, 0.9, 71],
  ];
  for (const [x, z, s, seed] of positions) trees.add(buildTree(x, z, s, seed));
  return trees;
}

function buildTree(x: number, z: number, scale: number, seed: number): THREE.Group {
  const rng = mulberry32(seed);
  const tree = new THREE.Group();
  tree.name = 'tree';
  const up = new THREE.Vector3(0, 1, 0);

  const barkGeos: THREE.BufferGeometry[] = [];
  const leafGeos: THREE.BufferGeometry[] = [];

  const addLeaves = (pos: THREE.Vector3) => {
    const k = 2 + Math.floor(rng() * 2);
    for (let i = 0; i < k; i++) {
      const p = pos
        .clone()
        .add(new THREE.Vector3((rng() - 0.5) * 0.55, (rng() - 0.15) * 0.55, (rng() - 0.5) * 0.55));
      const size = 0.55 + rng() * 0.45;
      for (let pl = 0; pl < 2; pl++) {
        const g = new THREE.PlaneGeometry(size, size);
        const q = new THREE.Quaternion().setFromEuler(
          new THREE.Euler(rng() * 6.28, rng() * 6.28, rng() * 6.28),
        );
        g.applyMatrix4(new THREE.Matrix4().compose(p, q, new THREE.Vector3(1, 1, 1)));
        leafGeos.push(g);
      }
    }
  };

  const branch = (origin: THREE.Vector3, dir: THREE.Vector3, length: number, radius: number, depth: number) => {
    const end = origin.clone().add(dir.clone().multiplyScalar(length));
    const g = new THREE.CylinderGeometry(radius * 0.6, radius, length, 5);
    const q = new THREE.Quaternion().setFromUnitVectors(up, dir.clone().normalize());
    g.applyMatrix4(
      new THREE.Matrix4().compose(origin.clone().add(end).multiplyScalar(0.5), q, new THREE.Vector3(1, 1, 1)),
    );
    barkGeos.push(g);

    if (depth <= 0) {
      addLeaves(end);
      return;
    }
    if (depth === 1) addLeaves(end);
    const n = depth >= 2 ? 3 : 2;
    for (let i = 0; i < n; i++) {
      const d2 = dir
        .clone()
        .add(new THREE.Vector3((rng() - 0.5) * 0.95, rng() * 0.5, (rng() - 0.5) * 0.95))
        .normalize();
      branch(end, d2, length * (0.6 + rng() * 0.16), radius * 0.66, depth - 1);
    }
  };

  const trunkH = 1.7 + rng() * 0.7;
  const lean = new THREE.Vector3((rng() - 0.5) * 0.12, 1, (rng() - 0.5) * 0.12).normalize();
  branch(new THREE.Vector3(0, 0, 0), lean, trunkH, 0.17, 3);

  const bark = mergeGeometries(barkGeos);
  if (bark) {
    const m = new THREE.Mesh(bark, getBarkMaterial());
    m.castShadow = true;
    tree.add(m);
  }
  const leaves = mergeGeometries(leafGeos);
  if (leaves) {
    const m = new THREE.Mesh(leaves, getLeafMaterial());
    m.castShadow = true;
    tree.add(m);
  }
  for (const g of barkGeos) g.dispose();
  for (const g of leafGeos) g.dispose();

  tree.position.set(x, terrainHeightAt(x, z), z);
  tree.scale.setScalar(scale);
  return tree;
}

// Materiales/textura de árbol compartidos (creados una vez).
let _barkMat: THREE.MeshStandardMaterial | null = null;
function getBarkMaterial(): THREE.MeshStandardMaterial {
  if (!_barkMat) _barkMat = new THREE.MeshStandardMaterial({ color: 0x8a7f70, roughness: 0.95 });
  return _barkMat;
}

let _leafMat: THREE.MeshStandardMaterial | null = null;
function getLeafMaterial(): THREE.MeshStandardMaterial {
  if (!_leafMat) {
    _leafMat = new THREE.MeshStandardMaterial({
      map: makeLeafTexture(),
      color: 0xdde7d8,
      alphaTest: 0.45,
      side: THREE.DoubleSide,
      roughness: 1,
      metalness: 0,
    });
  }
  return _leafMat;
}

/** Textura de racimo de hojas (canvas) con alfa para tarjetas de follaje. */
function makeLeafTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, 128, 128);
  const greens = ['#9db58f', '#acc09c', '#bccdab', '#8ea682', '#c7d4ba'];
  for (let i = 0; i < 34; i++) {
    const x = 64 + (Math.random() - 0.5) * 96;
    const y = 64 + (Math.random() - 0.5) * 96;
    const r = 9 + Math.random() * 16;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    const col = greens[Math.floor(Math.random() * greens.length)];
    grad.addColorStop(0, col);
    grad.addColorStop(0.65, col);
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** PRNG determinista (mulberry32) para árboles reproducibles. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
